import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useRights, useStore } from 'hooks';
import { IExecutorModel } from '../../store/models/algoritmsDirectoryStore';
import {
  AlgorithmBody,
  AnimatedAlgorithmBody,
  BookIcon,
  BookWrapper,
  FontButton,
  FontButtonsWrapper,
  InnerWrapper,
  LoaderWrapper,
  Placeholder,
  ProvidersTabs,
  ProviderTab,
  Wrapper,
} from './script.styled';
import { LinesLoader } from '../loaders';

type ScriptProps = {
  houseId: string;
};

export const Script: FC<ScriptProps> = observer(({ houseId }) => {
  const { script, user, algoritmsDirectory } = useStore();
  const { loading, getScript } = script;

  const createRequestInstructions = useRights('create_request_instructions');

  const [bigText, setBigText] = useState<boolean>(false);
  const [providersWithAlgorithms, setProvidersWithAlgorithms] = useState<
    IExecutorModel[]
  >([]);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(
    null
  );
  const [autoRotate, setAutoRotate] = useState<boolean>(true);
  const rotateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  // сохраняем высоту
  const [algHeight, setAlgHeight] = useState<number>(84);
  const algBodyRef = useRef<HTMLDivElement>(null);

  const providerToShow = useMemo(() => {
    return (
      providersWithAlgorithms.find(
        (p) => p.provider_id === selectedProviderId
      ) || null
    );
  }, [selectedProviderId, providersWithAlgorithms]);

  const algorithmToShow = useMemo(() => {
    if (!providerToShow) {
      return null;
    }

    return (
      providerToShow.algorithms.find((alg: any) =>
        alg.house_binding.houses.includes(houseId)
      ) || null
    );
  }, [providerToShow]);

  const handleProviderClick = (id: string) => {
    setSelectedProviderId(id);
    setAutoRotate(false); // отключаем автоматическую смену
  };

  useEffect(() => {
    if (!script.id && createRequestInstructions.read) {
      getScript(user.provider.id);
    }
  }, []);

  useEffect(() => {
    const list = algoritmsDirectory.getAlgorithmsByHouse(houseId);

    setProvidersWithAlgorithms(list);
    setSelectedProviderId(list[0]?.provider_id || null);
  }, [houseId]);

  useEffect(() => {
    if (!algoritmsDirectory.executorsList.length) {
      algoritmsDirectory.getAlgoritmsDirectoryExecutors();
    }
  }, []);

  // автоматическое переключение (анимация)
  useEffect(() => {
    if (!autoRotate || providersWithAlgorithms.length < 2) {
      return;
    }

    rotateTimerRef.current = setInterval(() => {
      setSelectedProviderId((prev) => {
        const currentIndex = providersWithAlgorithms.findIndex(
          (p) => p.provider_id === prev
        );
        const nextIndex = (currentIndex + 1) % providersWithAlgorithms.length;
        const nextId = providersWithAlgorithms[nextIndex]?.provider_id ?? null;

        return nextId;
      });
    }, 2000);

    return () => {
      if (rotateTimerRef.current) {
        clearInterval(rotateTimerRef.current);
      }
    };
  }, [autoRotate, providersWithAlgorithms]);

  // автоскролл вкладок
  useEffect(() => {
    if (!tabsRef.current) {
      return;
    }

    const parent = tabsRef.current;
    const activeTab = parent.querySelector<HTMLElement>('[data-active="true"]');

    if (!activeTab) {
      return;
    }

    const activeRect = activeTab.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    if (
      activeRect.left < parentRect.left ||
      activeRect.right > parentRect.right
    ) {
      parent.scrollTo({
        left:
          activeTab.offsetLeft -
          parent.clientWidth / 2 +
          activeTab.clientWidth / 2,
        behavior: 'smooth',
      });
    }
  }, [selectedProviderId]);

  // отслеживаем изменение высоты
  useEffect(() => {
    if (!algBodyRef.current) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const { height } = entries[0].contentRect;
      setAlgHeight(height);
    });

    observer.observe(algBodyRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <Wrapper canEdit={createRequestInstructions.update}>
      <ProvidersTabs ref={tabsRef}>
        {providersWithAlgorithms.map((provider) => (
          <ProviderTab
            key={provider.provider_id}
            isActive={provider.provider_id === selectedProviderId}
            data-active={provider.provider_id === selectedProviderId}
            onClick={() => handleProviderClick(provider.provider_id!)}
          >
            {provider.str_name}
          </ProviderTab>
        ))}
      </ProvidersTabs>

      <BookWrapper>
        <BookIcon />
      </BookWrapper>

      <FontButtonsWrapper>
        <FontButton
          isSelected={!bigText}
          variant={'small'}
          onClick={() => setBigText(false)}
        >
          A
        </FontButton>
        <FontButton
          isSelected={bigText}
          variant={'big'}
          onClick={() => setBigText(true)}
        >
          A
        </FontButton>
      </FontButtonsWrapper>

      {loading.pending ? (
        <LoaderWrapper>
          <LinesLoader size={'medium'} />
        </LoaderWrapper>
      ) : (
        <InnerWrapper>
          {!houseId ? (
            <Placeholder>
              Алгоритм появится после заполнения данных о заявителе
            </Placeholder>
          ) : (
            <>
              {algorithmToShow ? (
                <AnimatedAlgorithmBody
                  ref={algBodyRef}
                  style={{ height: algHeight }}
                >
                  <AlgorithmBody
                    isBig={bigText}
                    dangerouslySetInnerHTML={{ __html: algorithmToShow.body }}
                  />
                </AnimatedAlgorithmBody>
              ) : (
                <Placeholder>
                  Алгоритм для выбранного дома не найден
                </Placeholder>
              )}
            </>
          )}
        </InnerWrapper>
      )}
    </Wrapper>
  );
});
